import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Lock, MessageSquare } from 'lucide-react';
import { getArticleBySlug, trackArticleView, getErrorCode } from '@/services/api';
import { useBrand } from '@/contexts/BrandContext';
import { Breadcrumbs, ArticleContent, HelpfulWidget, LanguageSelector, type TocHeading } from '@/components';
import { formatDate, setReturnUrl } from '@/utils';
import { ErrorState, LoadingSpinner, Button } from '@airclou/ui';

export function ArticlePage() {
  const { slug, articleSlug, categorySlug, sectionSlug } = useParams<{ slug?: string; articleSlug?: string; categorySlug?: string; sectionSlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, brand } = useBrand();
  const navigate = useNavigate();
  
  // Support both old (/articles/:slug) and new (/:categorySlug/:sectionSlug/:articleSlug) formats
  const effectiveSlug = articleSlug || slug;
  
  // Get locale from URL param or use brand default
  const urlLocale = searchParams.get('locale');
  const [selectedLocale, setSelectedLocale] = useState<string | undefined>(urlLocale || undefined);
  const [tocHeadings, setTocHeadings] = useState<TocHeading[]>([]);

  const handleTocReady = useCallback((headings: TocHeading[]) => {
    setTocHeadings(headings);
  }, []);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['article', effectiveSlug, selectedLocale],
    queryFn: () => getArticleBySlug(effectiveSlug!, selectedLocale),
    enabled: !!effectiveSlug,
  });

  // Handle locale change
  const handleLocaleChange = (locale: string) => {
    setSelectedLocale(locale);
    // Update URL with new locale
    const newParams = new URLSearchParams(searchParams);
    newParams.set('locale', locale);
    setSearchParams(newParams, { replace: true });
  };

  const resolvedCategorySlug = data?.article?.category?.slug;
  const resolvedSectionSlug = data?.article?.section?.slug;
  
  // Redirect legacy URLs to clean format
  const shouldCanonicalRedirect =
    !!effectiveSlug &&
    !articleSlug &&  // Not already using new format
    !!resolvedCategorySlug &&
    !!resolvedSectionSlug;

  // Canonical redirect (legacy -> clean)
  useEffect(() => {
    if (!shouldCanonicalRedirect) return;

    navigate(
      `/${resolvedCategorySlug}/${resolvedSectionSlug}/${effectiveSlug}`,
      { replace: true }
    );
  }, [navigate, resolvedCategorySlug, resolvedSectionSlug, shouldCanonicalRedirect, effectiveSlug]);

  // Track view on mount
  useEffect(() => {
    if (shouldCanonicalRedirect) return;

    if (data?.article?.id) {
      trackArticleView(data.article.id).catch(() => {
        // Silently ignore view tracking errors
      });
    }
  }, [data?.article?.id, shouldCanonicalRedirect]);

  // Update page title
  useEffect(() => {
    if (shouldCanonicalRedirect) return;

    if (data?.article) {
      document.title = `${data.article.title} | Help Center`;
    }
  }, [data?.article, shouldCanonicalRedirect]);

  if (shouldCanonicalRedirect) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    const errorCode = getErrorCode(error);

    // Handle restricted content
    if (errorCode === 'AUTH_REQUIRED') {
      const handleSignIn = () => {
        setReturnUrl(window.location.pathname);
        navigate('/auth/login');
      };

      return (
        <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
          <Breadcrumbs
            items={[
              { label: 'Home', href: '/' },
              { label: 'Article' },
            ]}
          />
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Sign in required
            </h1>
            <p className="text-muted-foreground mb-6">
              This article requires you to sign in to view its content.
            </p>
            <Button onClick={handleSignIn}>Sign in to continue</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <ErrorState
          description={
            errorCode === 'NOT_FOUND'
              ? 'This article could not be found.'
              : 'Failed to load article. Please try again.'
          }
          action={
            errorCode !== 'NOT_FOUND' && (
              <button
                onClick={() => refetch()}
                className="text-primary hover:underline"
              >
                Try again
              </button>
            )
          }
        />
      </div>
    );
  }

  if (!data || !data.article) {
    return null;
  }

  const { article, related } = data;
  const section = article.section;
  const category = article.category;
  const localeInfo = data.locale_info;

  const canonicalArticleHref =
    category?.slug && section?.slug
      ? `/${category.slug}/${section.slug}/${article.slug}`
      : `/articles/${article.slug}`;

  const sectionHref =
    category?.slug && section?.slug
      ? `/${category.slug}/${section.slug}`
      : section?.slug
        ? `/sections/${section.slug}`
        : undefined;

  const showTOC = tocHeadings.length >= 3;

  return (
    <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          ...(category
            ? [{ label: category.name, href: `/${category.slug}` }]
            : []),
          ...(section && sectionHref ? [{ label: section.name, href: sectionHref }] : []),
          { label: article.title, href: canonicalArticleHref },
        ]}
      />

      <div className="lg:flex lg:gap-8">
        <article className="max-w-3xl flex-1 min-w-0">
          <header className="mb-8">
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {article.title}
              </h1>
              {/* Language selector - only show if multiple locales available */}
              {localeInfo && localeInfo.available.length > 1 && (
                <LanguageSelector
                  currentLocale={localeInfo.current}
                  availableLocales={localeInfo.available}
                  onLocaleChange={handleLocaleChange}
                />
              )}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Updated {formatDate(article.updated_at)}
            </p>
          </header>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <ArticleContent content={article.body} onTocReady={handleTocReady} />
          </div>

          {/* Helpful Widget */}
          <div className="mt-12 pt-8 border-t">
            <HelpfulWidget articleId={article.id} />
          </div>

          {/* Related Articles */}
          {related && related.length > 0 && (
            <div className="mt-8 pt-8 border-t">
              <h3 className="text-lg font-semibold mb-4">Related articles</h3>
              <ul className="space-y-2">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={
                        category?.slug && section?.slug
                          ? `/${category.slug}/${section.slug}/${item.slug}`
                          : `/articles/${item.slug}`
                      }
                      className="text-primary hover:underline"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact Support CTA */}
          {brand?.contact_email && (
            <div className="mt-8 p-6 rounded-lg bg-muted/50 text-center">
              <h3 className="font-semibold mb-2">Still need help?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our support team is here to assist you.
              </p>
              <a
                href={`mailto:${brand.contact_email}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] transition-colors"
              >
                <MessageSquare className="h-4 w-4" />
                Contact support
              </a>
            </div>
          )}
        </article>

        {/* Table of Contents sidebar */}
        {showTOC && (
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24">
              <h4 className="text-sm font-semibold mb-3 text-muted-foreground">
                On this page
              </h4>
              <nav className="space-y-1">
                {tocHeadings.map((heading, index) => (
                  <a
                    key={index}
                    href={`#${heading.id}`}
                    className="block text-sm text-muted-foreground hover:text-foreground transition-colors"
                    style={{ paddingLeft: `${(heading.level - 1) * 12}px` }}
                  >
                    {heading.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
